/*
 * macOS-style genie effect for the widget window, with a page fold at the far corner.
 *
 * Like macOS, the window is rasterized and the picture is warped, instead of moving live DOM:
 *   - snapshot: taken the moment the animation starts. Every running animation/transition in the
 *     window is paused and its current values are baked into the clone, together with hover/focus
 *     states, so the picture is pixel-identical to the window on screen. The clone is rendered via an
 *     SVG <foreignObject> with the widget's own CSS into a bitmap at the screen's pixel density.
 *   - canvas: one canvas, aligned to device pixels, replaces the window for the whole animation. The
 *     fold and the genie are both drawn on it; the window's drop shadow is a separate layer that
 *     fades out (minimize) or in (restore) while the window is still flat.
 *   - genie: each frame the picture is redrawn one pixel row at a time along an S-shaped funnel into
 *     the launcher (rows that land identically are merged into one draw). At rest positions rows
 *     are drawn 1:1, so the first and last frames match the real window exactly.
 *
 * Hand-overs between the real window and the canvas happen in one synchronous step, and the window's
 * final visibility is left in place for the caller's state change, so no frame ever shows both or
 * neither. Re-opening an unchanged window reuses its picture and seeks the window's animations to
 * the moment the picture was taken, so the hand-over at the end is seamless too.
 */

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const smoothstep = (edge0, edge1, x) => {
    const t = clamp01((x - edge0) / (edge1 - edge0));
    return t * t * (3 - 2 * t);
};
const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
const lerp = (a, b, t) => a + (b - a) * t;
const easeOutBack = (t) => 1 + 2.4 * (t - 1) ** 3 + 1.4 * (t - 1) ** 2;

const FOLD_MS = 300;
const UNFOLD_MS = 460;
const MINIMIZE_MS = 780;
const RESTORE_MS = 720;
const FALLBACK_MS = 420;
const SNAPSHOT_TIMEOUT_MS = 1200;
const XHTML_NS = 'http://www.w3.org/1999/xhtml';

// Values copied from the live window into the clone, so hover, focus and mid-animation states match.
const INLINE_PROPS = ['transform', 'opacity', 'color', 'background-color', 'background-position', 'border-color', 'box-shadow', 'fill', 'stroke', 'outline-color'];
const KEYFRAME_META = new Set(['offset', 'easing', 'composite', 'computedOffset']);

// Restore: flat-out flip from full fold, overshoot past flat (a small re-fold), settle.
const unfoldCurve = (t) => {
    if (t < 0.62) return 1 - easeInOutCubic(t / 0.62);
    if (t < 0.8) return 0.16 * smoothstep(0, 1, (t - 0.62) / 0.18);
    return 0.16 * (1 - smoothstep(0, 1, (t - 0.8) / 0.2));
};

const prefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const cornerOf = (container) =>
    ['bottom-left', 'top-right', 'top-left'].find((c) => container.classList.contains(`aaw-${c}`)) || 'bottom-right';

const foldSizeFor = (W, H) => Math.round(Math.min(150, W * 0.36, H * 0.3));

const toKebab = (prop) => (prop.startsWith('--') ? prop : prop.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`));

// @property rules are ignored inside a shadow root, so the animatable fold length is registered from
// script too (the maximize fold keyframes need it to interpolate). Registering twice throws; ignore.
try {
    window.CSS?.registerProperty?.({ name: '--aaw-fold', syntax: '<length>', inherits: true, initialValue: '0px' });
} catch (e) {
    // already registered
}

/* ---------- Widget CSS ---------- */

const cssCache = new WeakMap();

// The widget's rules (all `aaw-` prefixed) from the document or the embed's shadow root. Walking every
// stylesheet of the host app is slow, so the result is cached until the set of stylesheets changes.
const widgetRules = (node) => {
    const rootNode = node.getRootNode();
    const sheets = [...(rootNode.styleSheets || []), ...(rootNode.adoptedStyleSheets || [])];
    const cached = cssCache.get(rootNode);
    if (cached && cached.count === sheets.length && cached.rules.length) return cached.rules;

    const rules = [];
    sheets.forEach((sheet) => {
        let list;
        try {
            list = sheet.cssRules;
        } catch (e) {
            return; // cross-origin sheet
        }
        for (const rule of list) {
            // selectorText is cheap; only serialize plain style rules that belong to the widget.
            if (rule.selectorText !== undefined) {
                if (rule.selectorText.includes('aaw')) rules.push(rule);
            } else if (rule.cssText.includes('aaw')) {
                rules.push(rule);
            }
        }
    });
    cssCache.set(rootNode, { count: sheets.length, rules });
    return rules;
};

// Widget CSS as written (media queries intact), e.g. for mounting the widget in a shadow root.
export const collectWidgetCss = (node) => widgetRules(node).map((rule) => rule.cssText).join('\n');

// Widget CSS for the snapshot. Inside the SVG picture, media queries would be evaluated against the
// picture's own size (a 400px-wide window hits the phone layout), so each @media block is resolved
// against the real browser window now: matching blocks are unwrapped, the others dropped.
const snapshotCss = (node) => {
    const out = [];
    const add = (rule) => {
        if (typeof CSSMediaRule !== 'undefined' && rule instanceof CSSMediaRule) {
            if (window.matchMedia(rule.media.mediaText).matches) {
                for (const inner of rule.cssRules) add(inner);
            }
            return;
        }
        out.push(rule.cssText);
    };
    widgetRules(node).forEach(add);
    return out.join('\n');
};

/* ---------- Fold ---------- */

/*
 * Folded-over corner (the corner away from the launcher), drawn in the panel's CSS-pixel space onto a
 * context already scaled by `scale` device pixels per CSS pixel. Matches the CSS fold used for
 * maximize: the corner triangle is cut away and the flap (the back of the page) lies over the window,
 * clipped to the window's rounded shape, with the same shaded gradient and drop shadow.
 */
const drawFold = (ctx, W, H, size, corner, radius, scale) => {
    if (size <= 0.05) return;
    const fx = corner.endsWith('right') ? 0 : W;
    const fy = corner.startsWith('bottom') ? H : 0;
    const sx = fx === 0 ? 1 : -1;
    const sy = fy === 0 ? 1 : -1;
    const extent = Math.max(W, H) + 20;
    const r = Math.min(radius, size);

    ctx.save();
    ctx.translate(fx, fy);
    ctx.scale(sx, sy);

    // Cut the corner off.
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(size, 0);
    ctx.lineTo(0, size);
    ctx.closePath();
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';

    // The flap and its shadow stay inside the window: its rounded corner, minus the cut.
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.lineTo(extent, 0);
    ctx.lineTo(extent, extent);
    ctx.lineTo(0, extent);
    ctx.lineTo(0, r);
    if (r > 0) ctx.arcTo(0, 0, r, 0, r);
    ctx.closePath();
    ctx.clip();
    ctx.beginPath();
    ctx.moveTo(size, 0);
    ctx.lineTo(extent, 0);
    ctx.lineTo(extent, extent);
    ctx.lineTo(0, extent);
    ctx.lineTo(0, size);
    ctx.closePath();
    ctx.clip();

    const gradient = ctx.createLinearGradient(0, 0, size, size);
    gradient.addColorStop(0.5, '#b4bdc8');
    gradient.addColorStop(0.57, '#e3e8ee');
    gradient.addColorStop(0.82, '#ffffff');
    gradient.addColorStop(1, '#eef1f5');
    // Shadow offsets/blur are in device pixels and ignore the transform, hence `scale` and the signs.
    ctx.shadowColor = 'rgba(15, 23, 42, 0.32)';
    ctx.shadowBlur = 7 * scale;
    ctx.shadowOffsetX = 4 * scale * sx;
    ctx.shadowOffsetY = 4 * scale * sy;
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(size, 0);
    ctx.lineTo(0, size);
    ctx.lineTo(size, size);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
};

/* ---------- Snapshot ---------- */

const snapshotCache = new WeakMap();

const pathOf = (root, el) => {
    const path = [];
    let node = el;
    while (node && node !== root) {
        const parent = node.parentElement;
        if (!parent) return null;
        path.push(Array.prototype.indexOf.call(parent.children, node));
        node = parent;
    }
    return node === root ? path.reverse().join('.') : null;
};

const keyframeProps = (effect) => {
    const props = new Set();
    try {
        effect.getKeyframes().forEach((frame) => {
            Object.keys(frame).forEach((key) => {
                if (!KEYFRAME_META.has(key)) props.add(toKebab(key));
            });
        });
    } catch (e) {
        // no keyframes
    }
    return [...props];
};

// Pauses every running animation/transition in the window and records where each one is.
const freezeAnimations = (panel) => {
    let animations = [];
    try {
        animations = panel.getAnimations ? panel.getAnimations({ subtree: true }) : [];
    } catch (e) {
        animations = [];
    }
    return animations
        .filter((anim) => anim.effect && anim.effect.target)
        .map((anim) => {
            const running = anim.playState === 'running';
            if (running) anim.pause();
            return {
                anim,
                running,
                target: anim.effect.target,
                pseudo: anim.effect.pseudoElement || null,
                name: anim.animationName || null,
                time: anim.currentTime,
                props: keyframeProps(anim.effect),
                path: pathOf(panel, anim.effect.target),
            };
        });
};

const resumer = (frozen) => () => {
    frozen.forEach((rec) => {
        if (!rec.running) return;
        try {
            rec.anim.play();
        } catch (e) {
            // animation gone
        }
    });
};

// Re-opening with a cached picture: put the freshly restarted animations where they were when the
// picture was taken, and hold them there until the hand-over.
const seekAnimations = (panel, records) => {
    const byKey = new Map(records.filter((rec) => rec.name).map((rec) => [`${rec.path}|${rec.pseudo}|${rec.name}`, rec]));
    const frozen = freezeAnimations(panel);
    frozen.forEach((live) => {
        if (!live.name) return;
        const rec = byKey.get(`${live.path}|${live.pseudo}|${live.name}`);
        if (!rec || rec.time === null || rec.time === undefined) return;
        try {
            live.anim.currentTime = rec.time;
        } catch (e) {
            // not seekable
        }
    });
    return resumer(frozen);
};

// Synchronously builds the SVG markup of an exact clone of the (already frozen) window.
const buildSnapshotSvg = (panel, container, W, H, corner, frozen) => {
    // Unreadable stylesheets would give an unstyled picture; the caller falls back to a plain animation.
    const widgetCss = snapshotCss(container);
    if (!widgetCss.includes('.aaw-panel')) throw new Error('widget stylesheet is not readable');

    const panelHidden = getComputedStyle(panel).visibility === 'hidden';
    const clone = panel.cloneNode(true);
    clone.removeAttribute('id');
    clone.classList.remove('aaw-folding');
    clone.setAttribute('style', `position:absolute;left:0;top:0;right:auto;bottom:auto;width:${W}px;height:${H}px;display:flex;visibility:visible;transform:none;clip-path:none;animation:none;transition:none;box-shadow:none`);

    // Bake current values (hover, focus, paused animations and transitions) into the clone.
    const liveEls = panel.querySelectorAll('*');
    const cloneEls = clone.querySelectorAll('*');
    liveEls.forEach((el, i) => {
        const target = cloneEls[i];
        if (!target) return;
        const cs = getComputedStyle(el);
        INLINE_PROPS.forEach((prop) => {
            const value = cs.getPropertyValue(prop);
            if (value) target.style.setProperty(prop, value, 'important');
        });
    });

    const extraRules = [];
    let pseudoCount = 0;
    frozen.forEach((rec) => {
        if (rec.target === panel) return;
        const index = Array.prototype.indexOf.call(liveEls, rec.target);
        const target = cloneEls[index];
        if (index < 0 || !target) return;
        const cs = getComputedStyle(rec.target, rec.pseudo);
        const decls = rec.props
            .filter((prop) => !(panelHidden && prop === 'visibility'))
            .map((prop) => [prop, cs.getPropertyValue(prop)])
            .filter(([, value]) => value);
        if (!decls.length) return;
        if (rec.pseudo) {
            pseudoCount += 1;
            target.setAttribute('data-aaw-snap', String(pseudoCount));
            extraRules.push(`[data-aaw-snap="${pseudoCount}"]${rec.pseudo}{${decls.map(([prop, value]) => `${prop}:${value} !important`).join(';')}}`);
        } else {
            decls.forEach(([prop, value]) => target.style.setProperty(prop, value, 'important'));
        }
    });

    // The picture can't load external URLs, so a logo not yet inlined as a data: URL would show the
    // browser's broken-image placeholder; show its circle in the brand colour instead.
    clone.querySelectorAll('svg.aegis-logo-icon image').forEach((image) => {
        if ((image.getAttribute('href') || '').startsWith('data:')) return;
        image.parentNode.style.setProperty('background', 'var(--aaw-primary)', 'important');
        image.remove();
    });

    // cloneNode drops the scroll offset and the typed text.
    const body = panel.querySelector('.aaw-body');
    const cloneBody = clone.querySelector('.aaw-body');
    if (body && cloneBody && cloneBody.firstElementChild) {
        cloneBody.style.setProperty('overflow', 'hidden', 'important');
        cloneBody.firstElementChild.style.setProperty('transform', `translateY(${-body.scrollTop}px)`, 'important');
    }
    const input = panel.querySelector('textarea');
    const cloneInput = clone.querySelector('textarea');
    if (input && cloneInput) cloneInput.textContent = input.value;

    const wrap = document.createElement('div');
    wrap.className = `aaw-root aaw-${corner} aaw-snapshot${container.classList.contains('aaw-has-logo') ? ' aaw-has-logo' : ''}`;
    wrap.setAttribute('style', `${container.getAttribute('style') || ''};position:relative;top:auto;left:auto;width:${W}px;height:${H}px`);
    const style = document.createElement('style');
    style.textContent = `${widgetCss}
.aaw-snapshot *, .aaw-snapshot *::before, .aaw-snapshot *::after { animation: none !important; transition: none !important; }
${extraRules.join('\n')}`;
    wrap.appendChild(style);
    wrap.appendChild(clone);

    let markup = new XMLSerializer().serializeToString(wrap);
    if (!markup.includes(XHTML_NS)) markup = markup.replace('<div', `<div xmlns="${XHTML_NS}"`);
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}"><foreignObject x="0" y="0" width="100%" height="100%">${markup}</foreignObject></svg>`;
};

const toBitmap = async (canvas) => {
    if (typeof createImageBitmap !== 'function') return canvas;
    try {
        return await createImageBitmap(canvas);
    } catch (e) {
        return canvas;
    }
};

const rasterize = async (svg, W, H, dpr, foldArgs) => {
    const img = new Image();
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    await img.decode();

    const flat = document.createElement('canvas');
    flat.width = Math.round(W * dpr);
    flat.height = Math.round(H * dpr);
    flat.getContext('2d').drawImage(img, 0, 0, flat.width, flat.height);

    const folded = document.createElement('canvas');
    folded.width = flat.width;
    folded.height = flat.height;
    const ctx = folded.getContext('2d');
    ctx.drawImage(flat, 0, 0);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawFold(ctx, W, H, foldArgs.size, foldArgs.corner, foldArgs.radius, dpr);

    const [src, srcFolded] = await Promise.all([toBitmap(flat), toBitmap(folded)]);
    return { src, srcFolded };
};

const snapshotKey = (panel, W, H, dpr, corner) => [
    `${Math.round(W)}x${Math.round(H)}@${dpr}`,
    corner,
    panel.querySelector('.aaw-body')?.scrollTop || 0,
    panel.querySelector('textarea')?.value || '',
    panel.innerHTML,
].join('|');

/* ---------- Animation ---------- */

/**
 * @param {object} options
 * @param {HTMLElement} options.panel      the live window
 * @param {HTMLElement} options.target     the launcher the window funnels into / out of
 * @param {HTMLElement} options.container  the widget root (carries the corner class and theme)
 * @param {'minimize'|'restore'} options.mode
 * @param {() => void} options.onDone      called once the window is fully hidden / fully shown
 * @returns {() => void} cancel
 */
export function playGenie({ panel, target, container, mode, onDone }) {
    const minimize = mode === 'minimize';
    const panelRect = panel?.getBoundingClientRect();
    const targetRect = target?.getBoundingClientRect();
    if (!panelRect || !targetRect || !container || panelRect.width < 1 || panelRect.height < 1 || prefersReducedMotion()) {
        if (panel) panel.style.removeProperty('visibility');
        onDone();
        return () => {};
    }

    if (minimize) {
        // A focused input would show a caret/focus ring the picture can't match.
        const active = panel.getRootNode().activeElement;
        if (active && active !== panel && panel.contains(active) && active.blur) active.blur();
    } else {
        panel.style.visibility = 'hidden';
    }

    const dpr = window.devicePixelRatio || 1;
    const W = panelRect.width;
    const H = panelRect.height;
    const corner = cornerOf(container);
    const panelStyle = getComputedStyle(panel);
    const radius = parseFloat(panelStyle.borderTopLeftRadius) || 0;
    const boxShadow = panelStyle.boxShadow;
    const foldSize = foldSizeFor(W, H);
    const genieMs = minimize ? MINIMIZE_MS : RESTORE_MS;

    // Funnel into the middle of the round launcher, inside the circle: aiming at its square box lets
    // the last rows of the window poke out past the curve.
    const insetX = targetRect.width * 0.24;
    const insetY = targetRect.height * 0.24;
    const tL = targetRect.left + insetX - panelRect.left;
    const tR = targetRect.right - insetX - panelRect.left;
    const tTop = targetRect.top + insetY - panelRect.top;
    const tBot = targetRect.bottom - insetY - panelRect.top;
    const targetBelow = targetRect.top + targetRect.height / 2 > panelRect.top + H / 2;
    const funnelSpan = targetBelow ? Math.max(tTop, 1) : Math.max(H - tBot, 1);
    const funnelU = (y) => clamp01(targetBelow ? y / funnelSpan : (H - y) / funnelSpan);

    let cancelled = false;
    let frame = 0;
    let canvas = null;
    let ctx = null;
    let ghost = null;
    let fallbackAnimation = null;
    let resumeLive = () => {};

    const teardown = () => {
        cancelAnimationFrame(frame);
        canvas?.remove();
        ghost?.remove();
        canvas = null;
        ghost = null;
    };

    // One synchronous step: the window takes its final visibility in the same frame the canvas goes,
    // and keeps it (inline) until the caller's state change hides/shows it for good.
    const finish = () => {
        if (cancelled) return;
        cancelled = true;
        panel.style.visibility = minimize ? 'hidden' : 'visible';
        teardown();
        fallbackAnimation?.cancel();
        if (!minimize) resumeLive();
        onDone();
    };

    // requestAnimationFrame loop over [0, duration]; step(t) gets progress 0..1.
    const run = (duration, step, next) => {
        let start = null;
        const tick = (now) => {
            if (cancelled) return;
            if (start === null) start = now;
            const t = clamp01((now - start) / duration);
            step(t);
            if (t < 1) frame = requestAnimationFrame(tick);
            else next();
        };
        frame = requestAnimationFrame(tick);
    };

    // Simple, smooth scale into / out of the launcher when no picture can be taken.
    const runFallback = () => {
        if (cancelled) return;
        teardown();
        resumeLive();
        panel.style.visibility = 'visible';
        const frames = [
            { transform: 'none', opacity: 1 },
            { transform: 'scale(0.06)', opacity: 0 },
        ];
        fallbackAnimation = panel.animate(minimize ? frames : frames.reverse(), {
            duration: FALLBACK_MS,
            easing: minimize ? 'cubic-bezier(0.5, 0, 0.75, 0)' : 'cubic-bezier(0.25, 1, 0.5, 1)',
            fill: 'both',
        });
        fallbackAnimation.onfinish = finish;
    };

    // Canvas covering window + launcher, placed on whole device pixels so the picture maps 1:1.
    const pad = Math.ceil(6 * dpr);
    const bx0 = Math.floor(Math.min(panelRect.left, targetRect.left) * dpr) - pad;
    const by0 = Math.floor(Math.min(panelRect.top, targetRect.top) * dpr) - pad;
    const bx1 = Math.ceil(Math.max(panelRect.right, targetRect.right) * dpr) + pad;
    const by1 = Math.ceil(Math.max(panelRect.bottom, targetRect.bottom) * dpr) + pad;
    const originX = Math.round(panelRect.left * dpr) - bx0;
    const originY = Math.round(panelRect.top * dpr) - by0;

    const mountLayers = () => {
        // The window's drop shadow, as its own layer (the picture holds only the window itself).
        ghost = document.createElement('div');
        ghost.setAttribute('aria-hidden', 'true');
        Object.assign(ghost.style, {
            position: 'fixed',
            left: `${panelRect.left}px`,
            top: `${panelRect.top}px`,
            width: `${W}px`,
            height: `${H}px`,
            borderRadius: `${radius}px`,
            boxShadow,
            pointerEvents: 'none',
            opacity: minimize ? '1' : '0',
        });

        canvas = document.createElement('canvas');
        canvas.className = 'aaw-genie';
        canvas.setAttribute('aria-hidden', 'true');
        canvas.width = bx1 - bx0;
        canvas.height = by1 - by0;
        Object.assign(canvas.style, {
            left: `${bx0 / dpr}px`,
            top: `${by0 / dpr}px`,
            width: `${(bx1 - bx0) / dpr}px`,
            height: `${(by1 - by0) / dpr}px`,
        });
        ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Below the launcher, so the window drains into it rather than over it.
        const before = target.closest('.aaw-launcher-wrap') || null;
        container.insertBefore(ghost, before);
        container.insertBefore(canvas, before);
    };

    const beginFrame = () => {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.setTransform(dpr, 0, 0, dpr, originX, originY);
    };

    // The window at rest, pixel for pixel, with the corner folded by `amount` (0..1).
    const drawFlat = (src, amount) => {
        beginFrame();
        canvas.style.opacity = '1';
        ctx.drawImage(src, 0, 0, src.width, src.height, 0, 0, src.width / dpr, src.height / dpr);
        drawFold(ctx, W, H, amount * foldSize, corner, radius, dpr);
    };

    const rows = Math.ceil(H);
    const edgeY = new Float32Array(rows + 1);

    // Genie at progress p (0 = window at rest, 1 = inside the launcher).
    const drawGenie = (src, p) => {
        const curve = smoothstep(0, 0.42, p);
        for (let k = 0; k <= rows; k += 1) {
            const y = Math.min(k, H);
            const nearness = targetBelow ? y / H : 1 - y / H;
            // Rows closest to the launcher start flowing first.
            const start = 0.16 + 0.34 * (1 - nearness);
            const flow = easeInOutCubic(clamp01((p - start) / 0.5));
            edgeY[k] = lerp(y, lerp(tTop, tBot, y / H), flow);
        }

        beginFrame();
        // Rows are merged into one draw while their edges stay within a quarter pixel of the run's
        // first row and they keep the same vertical scale; untouched runs are drawn exactly 1:1.
        let runStart = 0;
        let runX0 = 0;
        let runX1 = W;
        let runScale = 1;
        const flush = (end) => {
            if (end <= runStart) return;
            const y0 = edgeY[runStart];
            const y1 = edgeY[end];
            const sy = runStart * dpr;
            const sh = Math.min(end * dpr, src.height) - sy;
            if (sh <= 0) return;
            const atRest = Math.abs(runX0) < 0.01 && Math.abs(runX1 - W) < 0.01 && Math.abs(runScale - 1) < 0.001 && Math.abs(y0 - runStart) < 0.01;
            if (atRest) {
                ctx.drawImage(src, 0, sy, src.width, sh, 0, runStart, src.width / dpr, sh / dpr);
            } else {
                // A hair of overlap keeps moving runs seamless; compressed rows simply stack.
                ctx.drawImage(src, 0, sy, src.width, sh, runX0, y0, runX1 - runX0, Math.max(y1 - y0, 0) + 0.35);
            }
        };
        for (let k = 0; k < rows; k += 1) {
            const y0 = edgeY[k];
            const y1 = edgeY[k + 1];
            const bend = smoothstep(0, 1, funnelU((y0 + y1) / 2)) * curve;
            const x0 = lerp(0, tL, bend);
            const x1 = lerp(W, tR, bend);
            const scale = y1 - y0;
            if (k === 0) {
                runX0 = x0;
                runX1 = x1;
                runScale = scale;
            } else if (Math.abs(x0 - runX0) > 0.25 || Math.abs(x1 - runX1) > 0.25 || Math.abs(scale - runScale) > 0.02) {
                flush(k);
                runStart = k;
                runX0 = x0;
                runX1 = x1;
                runScale = scale;
            }
        }
        flush(rows);
        canvas.style.opacity = String(1 - smoothstep(0.88, 1, p));
    };

    // Picture of the window: reused when re-opening an unchanged window, otherwise taken now.
    const key = snapshotKey(panel, W, H, dpr, corner);
    const cached = snapshotCache.get(panel);
    let snapshotPromise;
    if (!minimize && cached && cached.key === key) {
        resumeLive = seekAnimations(panel, cached.records);
        snapshotPromise = Promise.resolve(cached);
    } else {
        try {
            const frozen = freezeAnimations(panel);
            resumeLive = resumer(frozen);
            const svg = buildSnapshotSvg(panel, container, W, H, corner, frozen);
            const records = frozen.map(({ path, pseudo, name, time, running }) => ({ path, pseudo, name, time, running }));
            const work = rasterize(svg, W, H, dpr, { size: foldSize, corner, radius })
                .then(({ src, srcFolded }) => {
                    const entry = { key, src, srcFolded, records };
                    snapshotCache.set(panel, entry);
                    return entry;
                })
                .catch((error) => {
                    console.warn('[Aegis Assist] genie snapshot failed, using a simple animation', error);
                    return null;
                });
            const timeout = new Promise((resolve) => setTimeout(() => resolve(null), SNAPSHOT_TIMEOUT_MS));
            snapshotPromise = Promise.race([work, timeout]);
        } catch (error) {
            console.warn('[Aegis Assist] genie snapshot failed, using a simple animation', error);
            snapshotPromise = Promise.resolve(null);
        }
    }

    snapshotPromise.then((snap) => {
        if (cancelled) return;
        if (!snap) {
            runFallback();
            return;
        }
        mountLayers();

        if (minimize) {
            // Hand-over: the canvas shows the window exactly as it is; hide the real one in the same step.
            drawFlat(snap.src, 0);
            panel.style.visibility = 'hidden';
            run(FOLD_MS, (t) => {
                drawFlat(snap.src, easeOutBack(t));
                ghost.style.opacity = String(1 - smoothstep(0, 1, t));
            }, () => {
                ghost.style.opacity = '0';
                run(genieMs, (t) => drawGenie(snap.srcFolded, t), finish);
            });
        } else {
            // Pour out folded, flip the corner open while the shadow fades in, then hand over.
            drawGenie(snap.srcFolded, 1);
            run(genieMs, (t) => drawGenie(snap.srcFolded, 1 - t), () => {
                run(UNFOLD_MS, (t) => {
                    drawFlat(snap.src, unfoldCurve(t));
                    ghost.style.opacity = String(smoothstep(0, 1, t));
                }, finish);
            });
        }
    });

    return () => {
        if (cancelled) return;
        cancelled = true;
        teardown();
        fallbackAnimation?.cancel();
        panel.style.removeProperty('visibility');
        resumeLive();
    };
}
